import React, { useMemo, useRef, useState } from 'react';
import { FiLayers, FiSettings } from 'react-icons/fi';
import { normalizeVarietyGroups } from '../utils/varietyGroups';
import VarietyGroupConfigurationModal from './VarietyGroupConfigurationModal';

const VarietyGroupManager = ({
  branchId,
  product,
  canUpdateProduct = true,
  activeGroupId,
  onCreateVariety,
}) => {
  const [isConfigurationOpen, setIsConfigurationOpen] = useState(false);
  const openButtonRef = useRef(null);
  const groups = useMemo(
    () => normalizeVarietyGroups(product?.VarietyGroups),
    [product?.VarietyGroups]
  );
  const varieties = Array.isArray(product?.Varieties) ? product.Varieties : [];
  const assignedVarietyIds = useMemo(() => new Set(
    groups.flatMap((group) => (group.varietyIds || []).map(Number))
  ), [groups]);
  const unassignedCount = varieties.filter(
    (variety) => !assignedVarietyIds.has(Number(variety?.id))
  ).length;

  if (!product?.id) return null;

  const closeConfiguration = () => {
    setIsConfigurationOpen(false);
    window.setTimeout(() => openButtonRef.current?.focus(), 0);
  };

  const canOpen = canUpdateProduct || groups.length > 0;

  return (
    <>
      <section className="mb-4 rounded-xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 text-left">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-blue-100 text-blue-700">
              <FiLayers size={20} aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900">Grupos de variedades</h3>
              <p className="mt-0.5 text-xs text-gray-500">
                {groups.length
                  ? `${groups.length} ${groups.length === 1 ? 'grupo configurado' : 'grupos configurados'} · ${unassignedCount} ${unassignedCount === 1 ? 'variedad sin grupo' : 'variedades sin grupo'}`
                  : 'Crea pestañas para encontrar y mostrar las variedades más fácilmente.'}
              </p>
            </div>
          </div>

          {canOpen && (
            <button
              ref={openButtonRef}
              type="button"
              onClick={() => setIsConfigurationOpen(true)}
              className="inline-flex flex-none items-center justify-center gap-2 rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-semibold text-blue-700 shadow-sm hover:border-blue-300 hover:bg-blue-50"
            >
              <FiSettings aria-hidden="true" />
              {canUpdateProduct ? 'Configurar grupos' : 'Ver grupos'}
            </button>
          )}
        </div>

        {groups.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" aria-label="Resumen de grupos de variedades">
            {groups.slice(0, 5).map((group) => (
              <span
                key={group.id}
                className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700"
              >
                <span className="max-w-[180px] truncate font-medium">{group.name}</span>
                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
                  {(group.varietyIds || []).length}
                </span>
              </span>
            ))}
            {groups.length > 5 && (
              <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-500">
                +{groups.length - 5} más
              </span>
            )}
          </div>
        )}
      </section>

      <VarietyGroupConfigurationModal
        isOpen={isConfigurationOpen}
        onClose={closeConfiguration}
        branchId={branchId}
        product={product}
        canUpdateProduct={canUpdateProduct}
        initialGroupId={activeGroupId}
        onCreateVariety={onCreateVariety ? (groupId) => {
          setIsConfigurationOpen(false);
          onCreateVariety(groupId);
        } : undefined}
      />
    </>
  );
};

export default VarietyGroupManager;
